import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClipboardCheck, Home, AlertTriangle, Zap, Clock, CalendarClock, UserX } from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import type { Bien } from "@/types/bien";
import { cn } from "@/lib/utils";
import { today, todayIso as todayIsoFn } from "@/lib/today";
import { useAuth } from "@/contexts/AuthContext";
import { isForRent } from "@/lib/transaction";
import { isPebExpiringSoon } from "@/lib/pebCycle";
import { computeStatutEls } from "@/lib/elsStatus";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isoToDate = (iso: string): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const MONTH_SHORT = ["jan","fév","mar","avr","mai","jun","jul","aoû","sep","oct","nov","déc"];

// ─── Main dashboard ──────────────────────────────────────────────────────────

export function CollaborateurDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const currentCollabId = profile?.id ?? "";
  const { data: biensData } = useBiens();
  const biens = useMemo(() => (biensData ?? []) as unknown as Bien[], [biensData]);

  const myBiens = useMemo(() =>
    biens.filter((b) => isForRent(b.transaction) && b.collaborateurSrs === currentCollabId),
  [biens, currentCollabId]);

  const appartsForRent = useMemo(() =>
    biens.filter((b) =>
      isForRent(b.transaction) &&
      (b.disponibilite === "EN PRÉPARATION" || b.disponibilite === "DISPONIBLE")
    ),
  [biens]);

  const activeEls = useMemo(() =>
    myBiens.filter((b) => !b.dossierCloture && !b.dossierClos &&
      (b.disponibilite === "EN PRÉPARATION" || b.disponibilite === "DISPONIBLE")),
  [myBiens]);

  const activeElsCount = activeEls.length;

  const alertCounts = useMemo(() => {
    let peb = 0, noDate = 0, transfert = 0, newElsExpert = 0;
    activeEls.forEach((b) => {
      if (isPebExpiringSoon(b.pebFinLe, b.classePeb)) peb++;
      // `statutEls` retiré (mig. 112) : colonne vide sur les 1574 biens, donc
      // ces deux tuiles affichaient ZÉRO en permanence. `useElsAlertCounts`,
      // l'équivalent V2, utilisait déjà le statut calculé et fonctionnait.
      const els = computeStatutEls(b);
      if (els === "avertit") noDate++;
      if (els === "els_realise") {
        const d = isoToDate(b.edlDatePrevue);
        if (d && (today().getTime() - d.getTime()) / 86400000 >= 2 && !b.transfertElecFait) transfert++;
      }
      // Migration 103 : `jeNaiPasFaitEle` n'a jamais eu de colonne — la tuile
      // « nouveau prestataire à désigner » affichait donc zéro en permanence.
      // On lit la vraie colonne écrite par la case, `eleNonFait`.
      if (b.eleNonFait) newElsExpert++;
    });
    return { peb, noDate, transfert, newElsExpert };
  }, [activeEls]);

  // Upcoming agenda events: ELS with a fixed date (date_fixee or higher) that have edlDatePrevue
  const agendaEvents = useMemo(() => {
    const events: { id: string; ref: string; adresse: string; date: string; heure: string; type: "els" }[] = [];
    const todayStr = todayIsoFn();
    myBiens.forEach((b) => {
      if (b.edlDatePrevue && b.edlDatePrevue >= todayStr && ["date_fixee","els_realise","transfert_fait"].includes(computeStatutEls(b))) {
        events.push({
          id: b.id,
          ref: b.referenceInterne || b.id,
          adresse: [b.ville, b.adresse].filter(Boolean).join(" · "),
          date: b.edlDatePrevue,
          heure: b.edlHeure || "",
          type: "els",
        });
      }
    });
    // Sort by date ascending
    return events.sort((a, b) => a.date.localeCompare(b.date));
  }, [myBiens]);

  const totalAlerts = alertCounts.peb + alertCounts.noDate + alertCounts.transfert + alertCounts.newElsExpert;

  return (
    <div className="flex flex-col gap-6">

      {/* ── Row 1 — Summary cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => navigate("/suivi-els")}
          className="bg-surface border border-border rounded-lg p-3 md:p-5 text-left hover:border-accent transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2 mb-2 md:mb-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <ClipboardCheck size={16} className="text-accent" />
            </div>
            <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-text-muted">{t("collaborateur.suiviEls")}</span>
          </div>
          <div className="flex items-end gap-1.5">
            <span className="text-2xl md:text-3xl font-bold text-text-primary">{activeElsCount}</span>
            <span className="text-xs text-text-muted mb-0.5">{activeElsCount !== 1 ? t("common.files") : t("common.file")}</span>
          </div>
          <p className="text-[10px] md:text-xs text-text-muted mt-1">{t("collaborateur.notClosed")}</p>
        </button>

        <button
          type="button"
          onClick={() => navigate("/collaborateur/apparts")}
          className="bg-surface border border-border rounded-lg p-3 md:p-5 text-left hover:border-accent transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2 mb-2 md:mb-3">
            <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <Home size={16} className="text-success" />
            </div>
            <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-text-muted">{t("collaborateur.forRent")}</span>
          </div>
          <div className="flex items-end gap-1.5">
            <span className="text-2xl md:text-3xl font-bold text-text-primary">{appartsForRent.length}</span>
            <span className="text-xs text-text-muted mb-0.5">{appartsForRent.length !== 1 ? t("common.apartments") : t("common.apartment")}</span>
          </div>
          <p className="text-[10px] md:text-xs text-text-muted mt-1">{t("collaborateur.availableOrPrep")}</p>
        </button>
      </div>

      {/* ── Row 2 — Alert summary ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={14} className={totalAlerts > 0 ? "text-danger" : "text-text-muted"} />
          <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            {t("collaborateur.alertsToResolve")}
            {totalAlerts > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-danger text-white text-[10px] font-bold">{totalAlerts}</span>
            )}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button type="button" onClick={() => navigate("/suivi-els?filter=peb")}
            className={cn("rounded-lg p-2.5 md:p-4 border text-left cursor-pointer transition-colors",
              alertCounts.peb > 0 ? "bg-danger/5 border-danger/20 hover:bg-danger/10" : "bg-surface border-border opacity-50")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <Zap size={13} className={alertCounts.peb > 0 ? "text-danger" : "text-text-muted"} />
              <span className={cn("text-xl md:text-2xl font-bold", alertCounts.peb > 0 ? "text-danger" : "text-text-muted")}>{alertCounts.peb}</span>
            </div>
            <p className="text-[10px] md:text-xs font-medium text-text-secondary leading-tight">{t("collaborateur.pebMissing")}</p>
            <p className="text-[9px] md:text-[10px] text-text-muted mt-0.5 leading-tight">{t("collaborateur.lessThan6Months")}</p>
          </button>

          <button type="button" onClick={() => navigate("/suivi-els?filter=noDate")}
            className={cn("rounded-lg p-2.5 md:p-4 border text-left cursor-pointer transition-colors",
              alertCounts.noDate > 0 ? "bg-warning/5 border-warning/20 hover:bg-warning/10" : "bg-surface border-border opacity-50")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <Clock size={13} className={alertCounts.noDate > 0 ? "text-warning" : "text-text-muted"} />
              <span className={cn("text-xl md:text-2xl font-bold", alertCounts.noDate > 0 ? "text-warning" : "text-text-muted")}>{alertCounts.noDate}</span>
            </div>
            <p className="text-[10px] md:text-xs font-medium text-text-secondary leading-tight">{t("collaborateur.noElsDate")}</p>
            <p className="text-[9px] md:text-[10px] text-text-muted mt-0.5 leading-tight">{t("collaborateur.toContact")}</p>
          </button>

          <button type="button" onClick={() => navigate("/suivi-els?filter=transfert")}
            className={cn("rounded-lg p-2.5 md:p-4 border text-left cursor-pointer transition-colors",
              alertCounts.transfert > 0 ? "bg-danger/5 border-danger/20 hover:bg-danger/10" : "bg-surface border-border opacity-50")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <AlertTriangle size={13} className={alertCounts.transfert > 0 ? "text-danger" : "text-text-muted"} />
              <span className={cn("text-xl md:text-2xl font-bold", alertCounts.transfert > 0 ? "text-danger" : "text-text-muted")}>{alertCounts.transfert}</span>
            </div>
            <p className="text-[10px] md:text-xs font-medium text-text-secondary leading-tight">{t("collaborateur.elecTransfer")}</p>
            <p className="text-[9px] md:text-[10px] text-text-muted mt-0.5 leading-tight">{t("collaborateur.j2SinceEls")}</p>
          </button>

          <button type="button" onClick={() => navigate("/suivi-els?filter=newElsExpert")}
            className={cn("rounded-lg p-2.5 md:p-4 border text-left cursor-pointer transition-colors",
              alertCounts.newElsExpert > 0 ? "bg-danger/5 border-danger/20 hover:bg-danger/10" : "bg-surface border-border opacity-50")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <UserX size={13} className={alertCounts.newElsExpert > 0 ? "text-danger" : "text-text-muted"} />
              <span className={cn("text-xl md:text-2xl font-bold", alertCounts.newElsExpert > 0 ? "text-danger" : "text-text-muted")}>{alertCounts.newElsExpert}</span>
            </div>
            <p className="text-[10px] md:text-xs font-medium text-text-secondary leading-tight">{t("collaborateur.newElsExpert")}</p>
            <p className="text-[9px] md:text-[10px] text-text-muted mt-0.5 leading-tight">{t("collaborateur.toReassign")}</p>
          </button>
        </div>
      </div>

      {/* ── Row 3 — Agenda (liste des visites/ELS prévus) ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock size={14} className="text-text-muted" />
          <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">{t("collaborateur.agenda")}</span>
        </div>

        {agendaEvents.length === 0 ? (
          <p className="text-xs text-text-muted py-3">{t("collaborateur.noVisitPlanned")}</p>
        ) : (
          <div className="flex flex-col divide-y divide-border border border-border rounded-lg overflow-hidden">
            {agendaEvents.map((ev) => {
              const d = isoToDate(ev.date)!;
              const isPast = d < today();
              return (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => navigate(`/suivi-els/${ev.id}`)}
                  className="flex items-center gap-3 px-4 py-3 bg-surface hover:bg-surface-2 transition-colors cursor-pointer text-left"
                >
                  {/* Date block */}
                  <div className={cn(
                    "flex-shrink-0 w-10 text-center rounded-md py-1",
                    isPast ? "bg-surface-2" : "bg-accent/10"
                  )}>
                    <div className={cn("text-lg font-bold leading-none", isPast ? "text-text-muted" : "text-accent")}>
                      {String(d.getDate()).padStart(2, "0")}
                    </div>
                    <div className={cn("text-[9px] uppercase font-medium", isPast ? "text-text-muted" : "text-accent/70")}>
                      {MONTH_SHORT[d.getMonth()]}
                    </div>
                    {ev.heure && (
                      <div className={cn("text-[9px] font-semibold mt-0.5", isPast ? "text-text-muted" : "text-accent")}>
                        {ev.heure}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-text-primary truncate">{ev.ref}</span>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-accent/10 text-accent flex-shrink-0">{t("collaborateur.elsConfirmed")}</span>
                    </div>
                    <p className="text-xs text-text-muted truncate mt-0.5">{ev.adresse}</p>
                  </div>
                  </button>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
